import { useMemo } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useQueryGetBaselineVsActual } from 'api/queries/getBaselineVsActual.query';
import { useQueryGetMilestoneFragnet } from 'api/queries/getMilestoneFragnet.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { getDecodedValue } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramProgressOverview/programProgressOverview.utils';
import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';

export const useProgressSummaryProvider = ({
    componentKey,
    widgetId,
    contractId,
    projectId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    localFilters,
}) => {
    const isPinboard = Boolean(widgetId);
    const source = isPinboard ? localFilters : globalFilterData;
    const { isGenerativeAi } = usePinboardWidgetWrapperContext();

    return {
        ui: {
            state: getState({ projectId, contractId, latestVersionId, compareVersionId, source }),
            pills: useGetPills(source, widgetId, latestVersionId),
        },
        api: {
            getBaselineVsActual: useQueryGetBaselineVsActual(
                getBaselineVsActualPayload({
                    contractId,
                    projectId,
                    latestVersionId,
                    compareVersionId,
                    source,
                    isGenerativeAi,
                    componentKey,
                    widgetId,
                }),
            ),
            getMilestoneFragnet: useQueryGetMilestoneFragnet(getMilestoneFragnetPayload({ latestVersionId })),
        },
    };
};

const useGetPills = (source, widgetId, latestVersionId) => {
    const pillsData = {
        progressMeasurement: source?.progressMeasurement,
        resource: source?.resource,
        resourceType: source?.resourceType,
        activityCodes: source?.activityCodes?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        wbs: source?.wbs,
    };

    const { pillsList: pills } = useGlobalFiltersHook({
        widgetId,
        latestVersionId,
        source: pillsData,
    });

    return pills;
};

const getBaselineVsActualPayload = ({
    contractId,
    projectId,
    latestVersionId,
    compareVersionId,
    source,
    isGenerativeAi,
    componentKey,
    widgetId,
}) => {
    return {
        contractId: contractId,
        projectId: projectId,
        actualMetaDataId: latestVersionId,
        baselineMetaDataId: compareVersionId,
        filter: {
            filters: getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.value?.isExclude)
                    .map((i) => i.value?.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.value?.isExclude)
                    .map((i) => i.value?.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags
                    ?.filter((item) => !item.value?.isExclude)
                    .map((i) => i.value?.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                    ?.filter((item) => item.value?.isExclude)
                    .map((i) => i.value?.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
                [FilterNames.RESOURCE_NAME_CARD_BOARD]: source?.resource,
                [FilterNames.RESOURCE_TYPE_CARD_BOARD]: source?.resourceType,
            }),
        },
        graphType: getDecodedValue(source?.progressMeasurement)?.progressMeasurement,
        isPartialCompletion: getDecodedValue(source?.progressMeasurement)?.partialCompletionCredit,
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId),
    };
};

const getMilestoneFragnetPayload = ({ latestVersionId }) => {
    return {
        latestVersion: latestVersionId,
        filter: '',
    };
};

const getState = ({ projectId, contractId, latestVersionId, compareVersionId, source }) => {
    const state = {
        project: {
            projectId,
            contractId,
            latestVersionId,
            compareVersionId,
        },
        globalFilter: {
            progressMeasurement: source?.progressMeasurement,
            resource: source?.resource,
            resourceType: source?.resourceType,
            activityCodes: source?.activityCodes,
            milestoneFragnet: source?.milestoneFragnet,
            tags: source?.tags,
            wbs: source?.wbs,
        },
    };

    return Object.assign({}, ...Object.values(state));
};
